// Hero.jsx — desktop: full-bleed cinematic video; mobile: crafted editorial hero
// with the film shown FULLY (uncropped) in a framed cinema strip.
import React from "react";
import { VIDEOS } from "./assets";
import { Btn, Eyebrow, Display } from "./Primitives";
import LazyVideo from "./LazyVideo";
import { useIsMobile } from "./hooks";

const TRUST = ["Fragrance-free", "Plant-derived", "Refillable"];

function TrustRow({ light }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, flexWrap: "wrap", marginTop: 20 }}>
      {TRUST.map((t, i) => (
        <React.Fragment key={t}>
          {i > 0 && <span style={{ width: 3, height: 3, borderRadius: "50%", background: light ? "rgba(255,255,255,.5)" : "var(--ink-3)" }} />}
          <span style={{ fontFamily: "var(--font-sans)", fontSize: 11.5, fontWeight: 600, letterSpacing: ".12em", textTransform: "uppercase", color: light ? "rgba(255,255,255,.8)" : "var(--ink-3)" }}>{t}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

function MobileHero() {
  return (
    <section id="hero" style={{ background: "var(--paper)", minHeight: "100svh", display: "flex", flexDirection: "column", justifyContent: "center", padding: "96px 0 40px" }}>
      <div style={{ padding: "0 22px" }}>
        <Eyebrow style={{ display: "block", marginBottom: 14 }}>Dish · Hand · Laundry</Eyebrow>
        <Display as="h1" size="clamp(38px,11.5vw,54px)" style={{ fontWeight: 400, lineHeight: 1.02, letterSpacing: "-.02em" }}>
          Skin-kind care for every <span className="k-serif-i">home.</span>
        </Display>
      </div>

      {/* film, fully visible in a framed 16:9 cinema strip */}
      <div style={{ position: "relative", margin: "26px 16px 0", aspectRatio: "16 / 9", borderRadius: 20, overflow: "hidden", border: "1px solid var(--line)", boxShadow: "0 26px 50px -28px rgba(39,35,32,.45)", background: "linear-gradient(135deg, #e9e2d6, #d9cebb)" }}>
        <LazyVideo src={VIDEOS.hero} eager testId="hero-video" radius={20} />
        <div style={{ position: "absolute", inset: 0, boxShadow: "inset 0 0 0 1px rgba(255,255,255,.18)", borderRadius: 20, pointerEvents: "none" }} />
      </div>

      <div style={{ padding: "0 22px", marginTop: 24 }}>
        <p style={{ color: "var(--ink-2)", fontSize: 16, lineHeight: 1.6, textAlign: "center", margin: "0 auto 4px", maxWidth: 380 }}>
          Thoughtful care for the things you touch every day. Less harsh. More considered.
        </p>
        <TrustRow />
      </div>
    </section>
  );
}

function DesktopHero({ onShop, onPhilosophy }) {
  return (
    <section id="hero" className="k-vid" style={{ minHeight: "100svh", display: "flex" }}>
      <LazyVideo src={VIDEOS.hero} eager testId="hero-video" />
      <div className="k-vid-scrim" style={{ background: "linear-gradient(180deg, rgba(28,24,20,.34) 0%, rgba(28,24,20,.12) 32%, rgba(28,24,20,.32) 70%, rgba(28,24,20,.62) 100%)" }} />

      <div className="k-vid-inner" style={{ position: "relative", zIndex: 2, width: "100%", maxWidth: 1480, margin: "0 auto", padding: "0 clamp(20px,4vw,56px) clamp(46px,7vh,86px)", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
        <div style={{ maxWidth: 760 }}>
          <div className="k-reveal in" style={{ transitionDelay: ".15s" }}>
            <Eyebrow style={{ color: "rgba(255,255,255,.82)", display: "block", marginBottom: 20 }}>Dish · Hand · Laundry</Eyebrow>
          </div>
          <div className="k-reveal in" style={{ transitionDelay: ".28s" }}>
            <Display size="clamp(46px,7.6vw,104px)" style={{ color: "#fff", fontWeight: 400, letterSpacing: "-.02em", lineHeight: 1.0, textShadow: "0 2px 40px rgba(20,16,12,.4)" }}>
              Skin-kind care<br />for every <span className="k-serif-i">home.</span>
            </Display>
          </div>
          <div className="k-reveal in" style={{ transitionDelay: ".42s" }}>
            <p style={{ color: "rgba(255,255,255,.9)", fontSize: "clamp(15px,1.5vw,18px)", lineHeight: 1.6, maxWidth: 470, marginTop: 24, fontWeight: 400 }}>
              Thoughtful dish, hand, and laundry wash for the things you touch every day. Less harsh. Less loud. More considered.
            </p>
          </div>
          <div className="k-reveal in" style={{ transitionDelay: ".56s", marginTop: 32, display: "flex", gap: 14, flexWrap: "wrap" }}>
            <Btn variant="light" size="lg" icon="arrow-right" onClick={onShop} testId="hero-shop-btn">Shop the range</Btn>
            <Btn variant="ghostLight" size="lg" onClick={onPhilosophy} testId="hero-philosophy-btn">Our philosophy</Btn>
          </div>
        </div>
      </div>

      <div style={{ position: "absolute", bottom: 24, left: "50%", transform: "translateX(-50%)", zIndex: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
        <span style={{ fontFamily: "var(--font-sans)", fontSize: 10.5, letterSpacing: ".24em", textTransform: "uppercase", color: "rgba(255,255,255,.7)" }}>Scroll</span>
        <span style={{ width: 1, height: 38, background: "linear-gradient(rgba(255,255,255,.8), transparent)" }} />
      </div>
    </section>
  );
}

export default function Hero(props) {
  const isMobile = useIsMobile();
  return isMobile ? <MobileHero /> : <DesktopHero {...props} />;
}
